import { has } from 'lodash';
import { reaction } from 'mobx';
import React, { useEffect, useLayoutEffect, useState } from 'react';
import { FB, FBFormProps, FBFormState } from '..';
import { SM } from '../../../App';
import { checkIsDocumentReceivable } from '../../documentRevision/helpers/checkDocumentGroup';

export let FBFormStateContext = React.createContext<any>({});

/** Live form values of a workspace, with keys cleared by dependent-field rules added as explicit empty values. */
export const getFormStateSnapshot = (workspaceId: string): any => {
  const context = (FBFormStateContext as any)._currentValue;
  // Read the values at call time: clearing a field replaces formState.values, so a reference taken at render is stale.
  const values = context?.[`${workspaceId}:formState`]?.getValues() ?? context?.[workspaceId];
  const withClearedFormInput = context?.[`${workspaceId}:withClearedFormInput`];
  return values && withClearedFormInput ? withClearedFormInput(values) : values;
};

export const withFBForm = <T extends FBFormProps>(
  Component: React.FC<T>,
): React.FC<T> => {
  const Comp = ({
    initialValues = {},
    workspaceMode,
    errorValidation = true,
    initialValidation = false,
    includeLocking = false,
    initializeMPIFromWorkspace = false,
    formState,
    schema,
    children,
    ...props
  }: T) => {
    const { workspaceState } = FB.useStores();
    const { _documentRevisionFormState } = SM.useStores();
    !formState && (formState = FB.useRef<FBFormState>(FBFormState, {
      schema,
      initialValues: initialValues?.formInput || initialValues,
      workspaceMode: workspaceMode || workspaceState?.mode || 'form',
      workspaceState,
    }));

    const needsMPIHydration = initializeMPIFromWorkspace || schema?.some(item => item.type === 'mpiprocedure');
    const [initializedFormState, setInitializedFormState] = useState<FBFormState>();
    useLayoutEffect(() => {
      if (!needsMPIHydration) { return; }
      const dispose = reaction(
        () => schema ?? (initializeMPIFromWorkspace ? workspaceState?.schema : undefined),
        (currentSchema) => formState?.initializeMPIValues(currentSchema),
        { fireImmediately: true },
      );
      setInitializedFormState(formState);
      return dispose;
    }, [formState, schema, workspaceState, initializeMPIFromWorkspace, needsMPIHydration]);
    const isFormInitialized = !needsMPIHydration || initializedFormState === formState;

    const isReceivableType
        = checkIsDocumentReceivable(_documentRevisionFormState?.document?.documentType?.groupOptions);
    if (!(isReceivableType && has(initialValues, 'po-items-built-in'))) {
      const formStateContext = FBFormStateContext as any;
      FBFormStateContext = React.createContext<any>({
        ...formStateContext?._currentValue,
        [workspaceState?.id ?? '']: formState?.getValues(),
        [`${workspaceState?.id ?? ''}:formState`]: formState,
        [`${workspaceState?.id ?? ''}:withClearedFormInput`]: workspaceState?.withClearedFormInput,
      });
    }

    formState.setIncludeLocking(includeLocking);

    useEffect(() => {
      if (!formState) { return; }
      formState.errorValidation = errorValidation;
      formState.workspaceMode = workspaceMode;
    }, [formState, errorValidation, workspaceMode]);

    useEffect(() => {
      if (!formState) { return; }
      formState.initialValues = initialValues;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialValues]);

    useEffect(() => {
      if (!initialValidation || workspaceMode === 'design' || !isFormInitialized) { return; }
      // Join the mount batch without keeping input validation alive after the inputs detach.
      return formState?.requestMountValidation(false);
    }, [formState, initialValidation, workspaceMode, isFormInitialized]);

    return Component({
      ...(props as T),
      initialValues,
      formState,
      workspaceMode,
      children: isFormInitialized ? children : undefined,
      schema: isFormInitialized ? schema : undefined,
      includeLocking,
    });
  };

  return (props: T) => Comp(props);
};
